import { Card, CardContent } from "@/shared/ui/card";

import type { GeoReport } from "../../../api/geoService";

function num(v: number): string {
  return Number.isInteger(v) ? String(v) : String(Math.round(v * 10) / 10);
}

function Bar({ pct, className }: { pct: number; className: string }) {
  return (
    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-muted">
      <div
        className={`h-full rounded-full ${className}`}
        style={{ width: `${Math.min(100, pct)}%` }}
      />
    </div>
  );
}

const KLASS_CHIP: Record<string, string> = {
  own: "border-primary/50 bg-primary/10 text-primary",
  competitor: "border-violet-500/50 bg-violet-500/10 text-violet-600",
  neutral: "border-border bg-muted text-muted-foreground",
};

/**
 * E2/E3 检索面(引用归因):双模分解 + SoC 引用声量占比 + 分引擎引用源归因 + 投放优先级三档。
 * 口径:模型印象=记忆探针正面提及率(五维可见度同款);现场检索=联网探针同口径;
 * 引用按标题提及归类(带你=你的内容/带竞品=竞品内容/其余=中立)。
 * 旧报告或未配联网引擎时整卡不渲染。
 */
export function SearchInsight({ report }: { report: GeoReport }) {
  const ins = report.search_insight;
  if (!ins?.available) return null;
  const soc = ins.soc;
  const priorities = ins.source_priorities;
  const memoryScore = report.dimensions?.visibility;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-3 p-6">
          <div className="text-sm font-medium">双模可见度 · 模型印象 vs 现场检索</div>
          <div className="text-xs text-muted-foreground">
            印象来自模型训练语料(更新以模型版本为周期,难运营);检索来自联网回答的真实引用,铺对内容周级见效——运营动作盯检索这条
          </div>
          <div className="grid gap-2">
            <div className="flex items-center gap-3 text-sm">
              <span className="w-20 text-muted-foreground">模型印象</span>
              <Bar pct={memoryScore ?? 0} className="bg-muted-foreground/50" />
              <span className="w-12 text-right tabular-nums font-medium">
                {num(memoryScore ?? 0)}
              </span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <span className="w-20 text-muted-foreground">现场检索</span>
              <Bar pct={ins.search_score ?? 0} className="bg-primary" />
              <span className="w-12 text-right tabular-nums font-medium">
                {num(ins.search_score ?? 0)}
              </span>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            口径:正面提及 / 探针总数 × 100(与五维可见度同款);联网探针 {ins.probe_count ?? 0}{" "}
            条(对比/决策高价值子集 ×{" "}
            {(ins.engines ?? []).map((e) => e.label).join("、") || "联网引擎"})
          </div>
        </CardContent>
      </Card>

      {soc?.available && (
        <Card>
          <CardContent className="space-y-3 p-6">
            <div className="text-sm font-medium">引用声量占比(SoC)</div>
            <div className="text-xs text-muted-foreground">
              联网回答引用的 {soc.total} 个页面在讲谁(按引用页标题提及归类)
            </div>
            <div className="grid gap-2 text-sm">
              <div className="flex items-center gap-3">
                <span className="w-24 text-muted-foreground">讲你的内容</span>
                <Bar pct={soc.own_pct} className="bg-primary" />
                <span className="w-14 text-right tabular-nums">{num(soc.own_pct)}%</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-24 text-muted-foreground">讲竞品的</span>
                <Bar pct={soc.competitor_pct} className="bg-violet-500" />
                <span className="w-14 text-right tabular-nums">{num(soc.competitor_pct)}%</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="w-24 text-muted-foreground">中立页面</span>
                <Bar pct={soc.neutral_pct} className="bg-muted-foreground/50" />
                <span className="w-14 text-right tabular-nums">{num(soc.neutral_pct)}%</span>
              </div>
            </div>
            {(soc.competitor_breakdown ?? []).length > 0 && (
              <div className="text-xs text-muted-foreground">
                竞品拆分:
                {(soc.competitor_breakdown ?? [])
                  .map(([name, count]) => `${name} ${count} 篇`)
                  .join(" · ")}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="space-y-3 p-6">
          <div className="text-sm font-medium">分引擎引用源归因</div>
          <div className="text-xs text-muted-foreground">
            该引擎联网作答时真实引用了哪些域名(青=讲你的 / 紫=讲竞品 / 灰=中立)
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="py-1.5 pr-3 font-medium">引擎</th>
                  <th className="py-1.5 pr-3 font-medium">引用来源</th>
                  <th className="py-1.5 text-right font-medium">讲你占比</th>
                </tr>
              </thead>
              <tbody>
                {(ins.engine_attribution ?? []).map((a) => (
                  <tr key={a.engine} className="border-t">
                    <td className="whitespace-nowrap py-2 pr-3">{a.label}</td>
                    <td className="py-2 pr-3">
                      {a.citations_supported ? (
                        <div className="flex flex-wrap gap-1">
                          {a.top_domains.map(([domain, count, klass]) => (
                            <span
                              key={domain}
                              className={`rounded-full border px-2 py-0.5 text-xs ${KLASS_CHIP[klass] ?? KLASS_CHIP.neutral}`}
                            >
                              {domain} ×{count}
                            </span>
                          ))}
                          {a.top_domains.length === 0 && (
                            <span className="text-xs text-muted-foreground">本轮无引用返回</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          该引擎不回传引用(仅进双模对照)
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {a.citations_supported ? `${num(a.own_pct)}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {priorities && (priorities.core.length > 0 || priorities.quality.length > 0) && (
        <Card>
          <CardContent className="space-y-3 p-6">
            <div className="text-sm font-medium">信源投放优先级(按被 AI 引用次数)</div>
            <div className="grid gap-2 text-sm">
              {(
                [
                  ["核心(优先投放)", priorities.core],
                  ["优质", priorities.quality],
                  ["补充", priorities.supplement],
                ] as [string, string[]][]
              ).map(
                ([label, domains]) =>
                  domains.length > 0 && (
                    <div key={label} className="flex flex-wrap items-center gap-2">
                      <span className="w-28 text-xs text-muted-foreground">{label}</span>
                      {domains.map((d) => (
                        <span key={d} className="rounded-full border px-2.5 py-0.5 text-xs">
                          {d}
                        </span>
                      ))}
                    </div>
                  )
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
